import React from 'react';
import { Box } from '@mui/material';
import { FaMeta } from 'react-icons/fa6';
import type { IconBaseProps } from 'react-icons';

// react-icons v5 types its icons as returning ReactNode, which TypeScript
// 4.9's JSX checker rejects (TS2786). Re-typing the one icon we use as a
// plain component is the standard fix until the project moves to TS 5.
const MetaGlyph = FaMeta as unknown as React.ComponentType<IconBaseProps>;

/**
 * Meta brand mark for the Leads screens.
 *
 * The glyph is Font Awesome's `meta` brand icon via react-icons — the
 * current Meta infinity mark, not the old Facebook "f". Two forms:
 *   <MetaIcon />   the bare glyph, for inline use (buttons, chips)
 *   <MetaBadge />  the glyph on a rounded gradient tile, for card headers
 */

// Meta's current brand blue and the gradient it uses on its own tiles.
export const META_BLUE = '#0081FB';
export const META_GRADIENT = 'linear-gradient(135deg, #0064E0 0%, #0081FB 55%, #00C6FF 100%)';

interface MetaIconProps {
  size?: number;
  color?: string;
  style?: React.CSSProperties;
}

export const MetaIcon: React.FC<MetaIconProps> = ({ size = 18, color = META_BLUE, style }) => (
  <MetaGlyph size={size} color={color} style={{ display: 'block', ...style }} aria-hidden="true" />
);

interface MetaBadgeProps {
  size?: number; // tile size in px; glyph scales with it
  radius?: number | string; // tile corner radius (default: circle)
}

export const MetaBadge: React.FC<MetaBadgeProps> = ({ size = 36, radius = '50%' }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: radius,
      background: META_GRADIENT,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      boxShadow: '0 4px 14px rgba(0, 129, 251, 0.35), inset 0 1px 0 rgba(255,255,255,0.25)',
    }}
  >
    <MetaIcon size={Math.round(size * 0.56)} color="#fff" />
  </Box>
);
